export type TextInputColor = 'default' | 'inverse'

export type TextInputSize = 'medium' | 'small'

export type TextInputStatus = 'default' | 'disabled' | 'error'

export type TextInputVariant = 'filled' | 'outlined'

export type TextInputSizes = {
  [key in TextInputSize]: string
}

export type TextInputVariantsWithSizes = {
  [key in TextInputVariant]: TextInputSizes
}

type TextInputStatuses = {
  [key in TextInputStatus]: string
}

type TextInputStatuseColors = {
  [key in TextInputStatus]: {
    [key in TextInputColor]: string
  }
}

type Adornments = {
  icon: string
  multilineSize: TextInputVariantsWithSizes
  size: TextInputVariantsWithSizes
  text: TextInputStatuseColors
}

type Labels = {
  focused: { [key in TextInputVariant]: string }
  inactive: { [key in TextInputVariant]: string }
  value: { [key in TextInputVariant]: string }
}

type LabelColors = {
  focused: { [key in TextInputVariant]: TextInputStatuseColors }
  inactive: { [key in TextInputVariant]: TextInputStatuseColors }
  value: { [key in TextInputVariant]: TextInputStatuseColors }
}

type Sizes = {
  [key in TextInputVariant]: TextInputSizes
}

export type TextInputVariantsWithStatuses = {
  [key in TextInputVariant]: TextInputStatuses
}

export type TextInputVariantsWithStatuseColors = {
  [key in TextInputVariant]: TextInputStatuseColors
}

export const labels: Labels = {
  focused: {
    filled: `leading-5 text-xs`,
    outlined: `leading-4.5 text-xs`,
  },
  inactive: {
    filled: 'leading-5 text-sm',
    outlined: 'leading-5 text-sm',
  },
  value: {
    filled: 'leading-5 text-xs',
    outlined: `leading-4.5 text-xs`,
  },
}

export const labelColors: LabelColors = {
  focused: {
    filled: {
      default: {
        default: `text-primary-main`,
        inverse: `text-primary-lighter`,
      },
      disabled: {
        default: `text-primary-main`,
        inverse: `text-primary-lighter`,
      },
      error: { default: `text-error-main`, inverse: `text-error-light` },
    },
    outlined: {
      default: {
        default: `bg-white-100 text-primary-main`,
        inverse: `bg-white-100 text-primary-main`,
      },
      disabled: {
        default: `bg-white-100 text-primary-main`,
        inverse: `bg-white-100 text-primary-main`,
      },
      error: {
        default: `bg-white-100 text-error-main`,
        inverse: `bg-white-100 text-error-main`,
      },
    },
  },
  inactive: {
    filled: {
      default: { default: 'text-black-60', inverse: 'text-white-60' },
      disabled: { default: 'text-black-30', inverse: 'text-white-30' },
      error: { default: 'text-black-60', inverse: 'text-white-60' },
    },
    outlined: {
      default: { default: 'text-black-60', inverse: 'text-black-60' },
      disabled: { default: 'text-black-30', inverse: 'text-black-30' },
      error: { default: 'text-black-60', inverse: 'text-black-60' },
    },
  },
  value: {
    filled: {
      default: { default: 'text-black-60', inverse: 'text-white-60' },
      disabled: { default: 'text-black-30', inverse: 'text-white-30' },
      error: { default: 'text-error-main', inverse: 'text-error-light' },
    },
    outlined: {
      default: {
        default: `bg-white-100 text-black-60`,
        inverse: `bg-white-100 text-black-60`,
      },
      disabled: {
        default: `bg-white-100 text-black-30`,
        inverse: `bg-white-100 text-black-30`,
      },
      error: {
        default: `bg-white-100 text-error-main`,
        inverse: `bg-white-100 text-error-main`,
      },
    },
  },
}

export const labelSpacing = {
  focused: {
    filled: {
      medium: `mt-1 ml-3`,
      small: `mt-0.5 ml-3`,
    },
    outlined: {
      medium: `px-1 -mt-2.25 ml-2`,
      small: `px-1 -mt-2.25 ml-2`,
    },
  },
  inactive: {
    filled: {
      medium: 'mt-4 ml-3',
      small: 'mt-3.25 ml-3',
    },
    outlined: {
      medium: 'mt-3.25 ml-3',
      small: 'mt-2.25 ml-3',
    },
  },
}

export const sizes: Sizes = {
  outlined: {
    medium: 'py-3 h-12',
    small: 'py-3 h-10',
  },
  filled: {
    medium: 'h-14 pt-3 pb-0',
    small: 'h-12 pt-3 pb-0',
  },
}

export const parentSizes: Sizes = {
  outlined: {
    medium: 'h-12',
    small: 'h-10',
  },
  filled: {
    medium: 'h-14',
    small: 'h-12',
  },
}

export const multilineSizes: Sizes = {
  outlined: {
    medium: 'w-full mt-3 pb-0',
    small: 'w-full mt-2 pb-0',
  },
  filled: {
    medium: 'w-full mt-6 pb-0',
    small: 'w-full mt-5 pb-0',
  },
}

export const inputVariants: TextInputStatuseColors = {
  default: {
    default: 'bg-transparent text-black-90',
    inverse: 'bg-transparent text-white-90',
  },
  disabled: {
    default: 'bg-transparent text-black-30',
    inverse: 'bg-transparent text-white-30',
  },
  error: {
    default: 'bg-transparent text-black-90',
    inverse: 'bg-transparent text-white-90',
  },
}

export const focusedStyles: TextInputVariantsWithStatuseColors = {
  filled: {
    default: {
      default: `focus-within:bg-white-100 focus-within:border-primary-main focus-within:text-primary-main`,
      inverse: `focus-within:bg-transparent focus-within:border-primary-lighter focus-within:text-primary-lighter`,
    },
    disabled: { default: '', inverse: '' },
    error: {
      default: `focus-within:border-error-main focus-within:text-error-main`,
      inverse: `focus-within:bg-transparent focus-within:border-error-light focus-within:text-error-light`,
    },
  },
  outlined: {
    default: {
      default: `focus-within:shadow-input-primary-main focus-within:border-primary-main focus-within:text-primary-main`,
      inverse: `focus-within:shadow-input-primary-main focus-within:border-primary-main focus-within:text-primary-main`,
    },
    disabled: { default: '', inverse: '' },
    error: {
      default: `focus-within:shadow-input-error-main focus-within:border-error-main focus-within:text-error-main`,
      inverse: `focus-within:shadow-input-error-main focus-within:border-error-main focus-within:text-error-main`,
    },
  },
}

export const appearFocusedStyles: TextInputVariantsWithStatuseColors = {
  filled: {
    default: {
      default: `bg-white-100 border-2 border-primary-main text-primary-main`,
      inverse: `bg-transparent border-2 border-primary-lighter text-primary-lighter`,
    },
    disabled: {
      default: 'bg-black-5 text-black-30 border-2 border-transparent',
      inverse: 'bg-white-15 text-white-30 border-2 border-transparent',
    },
    error: {
      default: `bg-error-lighter border-2 border-error-main text-error-main`,
      inverse: `bg-transparent border-2 border-error-light text-error-light`,
    },
  },
  outlined: {
    default: {
      default: `shadow-input-primary-main border border-solid border-primary-main text-primary-main`,
      inverse: `shadow-input-primary-main border border-solid border-primary-main text-primary-main`,
    },
    disabled: {
      default: 'border border-solid border-black-25 bg-white-100 text-black-30',
      inverse: 'border border-solid border-black-25 bg-white-100 text-black-30',
    },
    error: {
      default: `shadow-input-error-main border border-solid border-error-main text-error-main`,
      inverse: `shadow-input-error-main border border-solid border-error-main text-error-main`,
    },
  },
}

export const variants: TextInputVariantsWithStatuseColors = {
  filled: {
    default: {
      default: 'bg-black-5 border-2 border-transparent text-black-60',
      inverse: 'bg-white-15 border-2 border-transparent text-white-60',
    },
    disabled: {
      default: 'bg-black-5 text-black-30 border-2 border-transparent',
      inverse: 'bg-white-15 text-white-30 border-2 border-transparent',
    },
    error: {
      default: 'bg-error-lighter text-error-main border-2 border-error-main',
      inverse: 'bg-white-15 text-error-light border-2 border-error-light',
    },
  },
  outlined: {
    default: {
      default: 'border border-solid border-black-25 text-black-60',
      inverse: 'border border-solid border-black-25 text-black-60',
    },
    disabled: {
      default: `border border-solid border-black-25 bg-white-100 text-black-30`,
      inverse: `border border-solid border-black-25 bg-white-100 text-black-30`,
    },
    error: {
      default: `shadow-input-error-main border border-solid border-error-main text-error-main`,
      inverse: `shadow-input-error-main border border-solid border-error-main text-error-main`,
    },
  },
}

export const adornments: Adornments = {
  icon: 'w-5',
  multilineSize: {
    outlined: {
      medium: 'mt-3 h-6',
      small: 'mt-2 h-6',
    },
    filled: {
      medium: 'mt-6 h-6',
      small: 'mt-5 h-6',
    },
  },
  size: {
    outlined: {
      medium: 'py-3 h-12',
      small: 'py-3 h-10',
    },
    filled: {
      medium: 'h-14',
      small: 'h-12',
    },
  },
  text: {
    default: {
      default: 'text-sm text-black-60',
      inverse: 'text-sm text-white-60',
    },
    disabled: {
      default: 'text-sm text-black-30',
      inverse: 'text-sm text-white-30',
    },
    error: {
      default: 'text-sm text-black-60',
      inverse: 'text-sm text-white-60',
    },
  },
}

export const baseClasses = {
  container: 'flex flex-col w-full font-sans',
  end: 'flex-shrink-0 whitespace-normal flex items-center',
  help: 'text-xs font-normal tracking-wide pt-2',
  inputWrapper: 'flex relative rounded',
  input: `flex-grow min-w-0 px-3 text-sm font-normal tracking-wide rounded disabled:cursor-not-allowed focus:outline-none`,
  label: `absolute top-0 left-0 block truncate max-w-(full-6) overflow-y-auto font-normal tracking-wide transition-all duration-200 ease-in-out`,
  start: `flex-shrink-0 ml-3 whitespace-normal flex items-center`,
}
